<script lang="ts" setup>

import {getProductSeats, Price, Product} from "~app/stripe";
import StripePriceCard from "~components/sales/StripePriceCard.vue";
import {computed} from "vue";
import {formatCurrencyUSD} from "@/common/utils/l10n";
import {UserInfo} from "@/common/utils/auth";
import QuoteFormModal from "~components/QuoteFormModal.vue";

const props = defineProps<{
  user: UserInfo
  prices: Record<string, Price>
  email: string
  purchased: string[]
}>()

function planPoints(product: Product): string[] {
  const common = [
    "A fun way for your kids to read on their own",
    "K-5 Reading Centers Blended with Character Lessons, Science, History & Grammar",
    "Multisensory Reading Fluency Practice",
    "4 Levels of Interactive Questioning",
    "Immediate Feedback & Graphed Data",
    "Lexile Leveled Cold Read Passages",
    "Common Core Aligned",
    "Hundreds Of Leveled Worksheets",
    "Constructed Response Activities",
    "Weekly Friday Free Teacher Resources",
    "Teacher Training Resources"
  ]

  const seats = getProductSeats(product)

  return [
    ...(seats ? [`Up to ${seats} Individual Student Accounts`] : []),
    ...common
  ]
}

const sitePoints = [
  // "Includes a subscription for all of the teachers in your school.",
  "Unlimited Student Accounts",
  "K-5 Reading Centers Blended with Character Lessons, Science, History & Grammar",
  "Multisensory Reading Fluency Practice",
  "4 Levels of Interactive Questioning",
  "Immediate Feedback & Graphed Data",
  "Lexile Leveled Cold Read Passages",
  "Common Core Aligned",
  "Hundreds Of Leveled Worksheets",
  "Constructed Response Activities",
  "Weekly Friday Free Teacher Resources",
  "Teacher Training Resources"
]

// TODO: get these as props
const siteSeats = 20
const sitePrice = 215000
const siteAlaCartePrice = 15000
const siteExtraSeatPrice = 10000

const monthly = computed(() => props.prices['plan_classroom_monthly'])
const yearly = computed(() => props.prices['plan_classroom_yearly'])

</script>

<template>
  <main>
    <div class="product-cards">
      <StripePriceCard
        class="card-monthly"
        :price="monthly"
        :points="planPoints(monthly.product)"
        :user="user"
      >
        <template v-if="purchased.includes(monthly.id)" #cta>
          <div class="already-purchased">You have an active subscription of this type</div>
        </template>
      </StripePriceCard>

      <div class="column">
        <div class="best-value">Best Value!</div>
        <StripePriceCard
          class="card-yearly"
          :price="yearly"
          :points="planPoints(yearly.product)"
          :user="user"
        >
          <template v-if="purchased.includes(yearly.id)" #cta>
            <div class="already-purchased">You have an active subscription of this type</div>
          </template>
        </StripePriceCard>
      </div>

      <StripePriceCard
        class="card-site bg-dark-blue text-white"
        title="Full School License"
        :points="sitePoints"
      >
        <template #price>
          <div class="site-price-heading">
            {{ formatCurrencyUSD(sitePrice / 100) }}
            <i>&mdash;or&mdash;</i>
            {{ formatCurrencyUSD(siteAlaCartePrice / 100) }}/teacher
          </div>
          <div class="site-price-subhead">
            (Up to {{ siteSeats }} teachers, {{ formatCurrencyUSD(siteExtraSeatPrice / 100) }}/teacher thereafter)
          </div>
        </template>
        <template #cta>
          <QuoteFormModal product="school" btn-label="Get a Site License Quote" />
          <QuoteFormModal product="district" btn-label="Contact Us About District Licensing" />
        </template>
      </StripePriceCard>
    </div>

    <div class="bottom-notice alert-soft-info">
      <p><strong>NOTE:</strong> Monthly and Annual payments are renewed automatically.</p>
      <p>Need a P.O. for your school, want to inquire about school / district licensing, or just have some
        questions before making a decision? Please contact Jeremy Spartz directly at
        admin@lyrics2learn.com or (719) 645-8587 and he will contact you as soon as possible.</p>
    </div>
  </main>
</template>

<style scoped lang="scss">
@import "../../../sass/colorscheme";

main {
  margin-top: 2rem;
}

.product-cards {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
  gap: 1rem;

  * {
    min-width: 15rem;
    max-width: 25rem;
  }
}

.card-monthly {
  background-color: #eee;
}

.card-yearly {
  background-color: lighten($cs-l2l-light-yellow, 5%);
  height: 100%;
}

.column {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.best-value {
  background: $cs-l2l-purple;
  color: $cs-l2l-light-yellow;
  text-align: center;
  margin: -0.75rem 0.5rem;
  font-weight: bold;
  border-radius: 1rem;
  z-index: 1000;
}

.cta-button {
  max-width: 75%;
  margin: 2rem;
}

.title {
  font-size: 1.5rem;
  font-weight: bold;
}

.bottom-notice {
  margin: 1rem auto;
  padding: 0.5rem;
  border-radius: 1rem;
  max-width: 60rem;
}

.already-purchased {
  font-style: italic;
  background-color: lighten($cs-l2l-light-yellow, 5%);
  border: 2px solid $cs-l2l-light-purple;
  border-radius: 0.5rem;
  padding: 0.5rem;
  margin: 0.5rem;
}
</style>
